import { Button, Grid } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { batch, useDispatch, useSelector } from "react-redux";
import { errorAlert } from "../../helpers/alerts";
import { getProvidersToRuteoV2Service } from "../../services/admin/providerService";
import { getCommercesService } from "../../services/client/commerceService";
import { getOperatorsService } from "../../services/admin/operatorServices";
import { getSegmentsByOperatorService } from "../../services/admin/segmentServices";
import { getDenominationBySegmentService } from "../../services/admin/denominationServices";
import {
  setOperators,
  setSegments,
  setRegions,
  setDenominations,
} from "../../redux/slices/route/exceptionSlice";
import { setCommerces } from "../../redux/slices/client/commerceSlice";
import BackButton from "../BackButton";
import InputSelectFieldGrid from "../common/InputSelectFieldGrid";
import InputTextFieldGrid from "../common/InputTextFieldGrid";
import { sortArrayBy, validateEmptyFields } from "../../helpers/util";
import { getRegionsService } from "../../services/admin/ladasService";
import CustomButton from "components/CustomButton";
import { useNavigate } from "react-router-dom";

const ExceptionForm = ({ title, goBack, defaultValues, submit, validate }) => {
  const navigate = useNavigate();
  const { control, handleSubmit, setValue } = useForm({ defaultValues });
  const dispatch = useDispatch();
  const [disabled, setDisabled] = useState(true);
  const [disabledRegions, setDisabledRegions] = useState(false);
  // Un alta tarda en responder; sin este candado cada clic en «Guardar» manda otra
  // excepción idéntica (el 5 de octubre se crearon 18 iguales en producción).
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const watch = useWatch({ control });
  const {
    operators,
    regions,
    segments,
    denominations,
  } = useSelector((store) => store.exceptions);
  const { commerces } = useSelector((store) => store.commerces);
  const { operator, segment, secondaryProvider, primaryProvider } = watch;
  const [providers, setProviders] = useState([])

  const asList = (value) => (Array.isArray(value) ? value : []);
  const operatorList = asList(operators);
  const regionList = asList(regions);
  const segmentList = asList(segments);
  const denominationList = asList(denominations);
  const commerceList = asList(commerces);
  const providerList = asList(providers);

  useEffect(() => {
    (async () => {
      try {
        const operators_ = await getOperatorsService();
        const commerces_ = await getCommercesService(undefined, true);
        const providers_ = await getProvidersToRuteoV2Service();
        batch(() => {
          dispatch(setOperators(operators_));
          dispatch(setCommerces(commerces_));
          setProviders(providers_);
        });
      } catch (error) {
        errorAlert(error);
      }
    })();
    return () => {
      dispatch(setRegions([]));
      dispatch(setSegments([]));
      dispatch(setDenominations([]));
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (operator) {
      const item = operatorList.find(({ id }) => id === operator);
      getSegmentsByOperatorService(operator)
        .then((response) => dispatch(setSegments(response)))
        .catch(error => errorAlert(error))
      if (item?.regions === true) {
        getRegionsService()
          .then((response) => {
            dispatch(setRegions(sortArrayBy(asList(response), ({ nombre }) => nombre ?? '')));
            setDisabledRegions(false);
          }).catch(error => errorAlert(error))
      } else {
        dispatch(setRegions([]));
        setDisabledRegions(true);
      }
    } else {
      dispatch(setSegments([]))
      dispatch(setRegions([]));
    }
    // eslint-disable-next-line
  }, [operator]);

  useEffect(() => {
    if (segment)
      getDenominationBySegmentService(segment)
        .then((response) => dispatch(setDenominations(response)))
        .catch(error => errorAlert(error))
    else {
      dispatch(setDenominations([]));
    }
    // eslint-disable-next-line
  }, [segment]);

  const save = async (data) => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      await submit(data);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  useEffect(() => {
    setDisabled(
      validateEmptyFields(watch, {
        validate,
        skip_fields: [
          "operator",
          "region",
          "segment",
          "denomination",
          "commerce",
          "secondaryProvider",
          "amount"
        ],
      })
    );
    // eslint-disable-next-line
  }, [watch]);

  const validateProviders = (otherProvider) => {
    const available = providerList.filter(({ _id, operators }) => (
      asList(operators).some((id) => id === operator) && _id !== otherProvider
    ));
    return [
      {
        _id: null,
        container_name: operator && available.length === 0
          ? 'Ningún proveedor atiende esta operadora'
          : '',
      },
      ...available,
    ];
  }

  return (
    <>
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <div>
          <BackButton click={() => navigate('..')} />
        </div>
        <div className="container-title">
          <div className="title">Recargas Celular</div>
          <div className="subtitle">Excepciones</div>
        </div>
        <div style={{ width: "170px" }}></div>
      </Grid>
      <Grid container justifyContent="center" alignItems="center" mt={"40px"}>
        <Grid width={"70%"} container spacing={3}>
          <InputSelectFieldGrid
            name={"operator"}
            xs={6}
            control={control}
            required={"Debes seleccionar una operadora"}
            label={"Seleccionar operadora"}
            data={operatorList}
            formatter={(data) =>
              data?.map(({ id, name }) => ({ id: id, name: name }))
            }
            onSelect={() => {
              setValue('region', '')
              setValue('segment', '')
              setValue('denomination', '')
            }}
          />
          <InputSelectFieldGrid
            name={"region"}
            xs={6}
            control={control}
            label={"Seleccionar región"}
            displayEmpty={true}
            data={[{ id: '', nombre: 'Todas' }, ...regionList]}
            formatter={(data) =>
              data?.map(({ id, nombre }) => ({ id: id, name: nombre }))
            }
            multiple={true}
            disabled={disabledRegions}
            onSelect={() => {
              setValue('segment', '')
              setValue('denomination', '')
            }}
            render={(value) => value.map((id) => {
              const region = regionList.find(o => o.id === id);
              return region?.nombre;
            }).join(', ')}
          />
          <InputSelectFieldGrid
            name={"segment"}
            xs={6}
            control={control}
            // required={"Debes seleccionar un segmento"}
            label={"Seleccionar segmento"}
            displayEmpty={true}
            data={[{ id: '', name: 'Todos' }, ...segmentList]}
            formatter={(data) =>
              data?.map(({ id, name }) => ({ id: id, name: name }))
            }
            onSelect={() => {
              setValue('denomination', '')
            }}
          />
          <InputSelectFieldGrid
            name={"denomination"}
            xs={6}
            control={control}
            // required={"Debes seleccionar una denominación"}
            label={"Seleccionar denominación"}
            displayEmpty={true}
            data={denominationList}
            multiple={true}
            formatter={(data) =>
              data.map((item) => ({ id: item.id, name: item.denomination }))
            }
            render={(value) => value.map((id) => {
              const denomination = denominationList.find(o => o.id === id);
              return denomination?.denomination;
            }).join(', ')}
          />
          <InputSelectFieldGrid
            name={"commerce"}
            xs={12}
            control={control}
            // required={"Debes seleccionar un comercio"}
            label={"Seleccionar comercio"}
            displayEmpty={true}
            data={[{ _id: '', username: 'Todos' }, ...commerceList]}
            formatter={(data) =>
              data.map((item) => ({ id: item._id, name: item.username }))
            }
          />
          <InputSelectFieldGrid
            name={"primaryProvider"}
            xs={6}
            control={control}
            required={"Debes seleccionar el proverdo primario"}
            label={"Selecciona el proveerdor primario"}
            disabled={operator === ''}
            data={validateProviders(secondaryProvider)}
            formatter={(data) =>
              data?.map((item) => ({ id: item._id, name: item.container_name }))
            }
          />
          <InputSelectFieldGrid
            name={"secondaryProvider"}
            xs={6}
            control={control}
            // required={"Debes seleccionar el proverdo secundario"}
            label={"Selecciona el proveerdor secundario"}
            disabled={operator === ''}
            data={validateProviders(primaryProvider)}
            formatter={(data) =>
              data?.map((item) => ({ id: item._id, name: item.container_name }))
            }
          />
          <Grid container justifyContent="center" mt={3}>
            <InputTextFieldGrid
              name={"amount"}
              xs={4}
              control={control}
              // required={"Debes agregar la cantidad"}
              label={"Monto"}
              inputProps={{ type: 'number' }}
              inputType={"number"}
              step={"any"}
            />
          </Grid>
          <Grid container justifyContent="center" mt={5}>
            <CustomButton
              click={handleSubmit(save)}
              name={"Guardar"}
              disabled={disabled}
              loading={saving}
            />
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default ExceptionForm;
